/**
 * Calculates mastery discount for products marked as "mastery"
 * @param originalPrice - The original price of the product
 * @param maxMasteryDiscount - The maximum mastery discount percentage (0-100)
 * @returns The discounted price
 */
export function calculateMasteryDiscount(originalPrice: number, maxMasteryDiscount: number): number {
  if (maxMasteryDiscount <= 0) return originalPrice
  
  const discountedPrice = originalPrice * (100 - maxMasteryDiscount) / 100
  return Math.round(discountedPrice * 100) / 100 // Round to 2 decimal places
}

/**
 * Calculates the invoice price for any product
 * @param product - The product object
 * @returns The product with invoice_price calculated
 */
export function calculateInvoicePrice(product: any) {
  // Step 1: Use discount price only if discount_flag is set, otherwise use item.price
  let baseInvoicePrice
  
  if (product.discount_flag === 1 || product.discount_flag === true) {
    // Use discount price if discount_flag is set
    baseInvoicePrice = product.discount || 0
  } else {
    // Use regular price if discount_flag is not set
    baseInvoicePrice = product.price || 0
  }
  
  console.log('calculateInvoicePrice - Input:', {
    productName: product.card_name,
    originalPrice: product.price,
    originalDiscount: product.discount,
    discount_flag: product.discount_flag,
    isMastery: isMasteryProduct(product),
    calculatedInvoicePrice: baseInvoicePrice
  })
  
  return {
    ...product,
    invoice_price: baseInvoicePrice
  }
}

/**
 * Applies mastery discount to mastery products only
 * @param product - The product object (must already have invoice_price)
 * @param maxMasteryDiscount - The maximum mastery discount percentage (0-100)
 * @returns The product with mastery discount applied to invoice_price
 */
export function applyMasteryDiscount(product: any, maxMasteryDiscount: number) {
  console.log('applyMasteryDiscount - Input:', {
    productName: product.card_name,
    invoicePrice: product.invoice_price,
    maxMasteryDiscount,
    isMastery: isMasteryProduct(product)
  })
  
  if (maxMasteryDiscount <= 0) {
    console.log('applyMasteryDiscount - No discount applied (maxMasteryDiscount <= 0)')
    return product
  }
  
  // Only apply mastery discount to mastery products
  if (!isMasteryProduct(product)) {
    console.log('applyMasteryDiscount - No discount applied (not a mastery product)')
    return product
  }
  
  // Apply mastery discount to the existing invoice_price
  const masteryDiscountedPrice = calculateMasteryDiscount(product.invoice_price, maxMasteryDiscount)
  
  console.log('applyMasteryDiscount - Output:', {
    productName: product.card_name,
    originalInvoicePrice: product.invoice_price,
    masteryDiscountedPrice,
    maxMasteryDiscount,
    finalInvoicePrice: masteryDiscountedPrice
  })
  
  return {
    ...product,
    invoice_price: masteryDiscountedPrice, // Update invoice_price with mastery discount
    discount_flag: 1, // Enable discount display
    product_volume: calculateMasteryDiscount(product.product_volume, maxMasteryDiscount),
    rank_volume: calculateMasteryDiscount(product.rank_volume, maxMasteryDiscount),
    hpp_units: Math.floor(calculateMasteryDiscount(product.hpp_units, maxMasteryDiscount)),
    hpp_coupons: Math.floor(calculateMasteryDiscount(product.hpp_coupons, maxMasteryDiscount))
  }
}

/**
 * Gets the maximum mastery discount from selected base plan and global mastery discount
 * @param selectedBasePlan - The selected base plan object
 * @param globalMasteryDiscount - Global mastery discount from API response
 * @returns The maximum mastery discount percentage
 */
export function getMaxMasteryDiscount(selectedBasePlan: any, globalMasteryDiscount: number): number {
  let maxDiscount = globalMasteryDiscount || 0
  
  console.log('getMaxMasteryDiscount - Input:', {
    selectedBasePlan: selectedBasePlan ? { id: selectedBasePlan.id, card_name: selectedBasePlan.card_name, mastery_discount: selectedBasePlan.mastery_discount } : null,
    globalMasteryDiscount
  })
  
  // Use the mastery_discount from the selected base plan
  if (selectedBasePlan && selectedBasePlan.mastery_discount && selectedBasePlan.mastery_discount > maxDiscount) {
    maxDiscount = selectedBasePlan.mastery_discount
  }
  
  console.log('getMaxMasteryDiscount - Result:', maxDiscount)
  return maxDiscount
}

/**
 * Checks if a product is marked as "mastery" (addons or addons_addons)
 * @param product - The product object
 * @returns True if the product should have mastery discount applied
 */
export function isMasteryProduct(product: any): boolean {
  // Check if the product has is_mastery = true
  const isMastery = product.is_mastery === true || product.is_mastery === 1
  
  console.log('isMasteryProduct - Checking:', {
    productName: product.card_name,
    is_mastery: product.is_mastery,
    isMasteryResult: isMastery
  })
  
  return isMastery
}

/**
 * Gets the invoice price from a product
 * @param product - The product object
 * @returns The invoice price (with fallback calculation if needed)
 */
export function getInvoicePrice(product: any): number {
  // If invoice_price exists, use it
  if (product.invoice_price !== undefined) {
    return product.invoice_price
  }
  
  // Fallback: calculate invoice price on the fly
  return product.discount || product.price
}
